import { HTMLAttributes, useEffect } from 'react';
import { ControllerRenderProps } from 'react-hook-form';

import { ComboboxModel } from '../../../combobox/combobox-model';
import { Attribute } from '../attribute';
import { FieldWrapper } from '../field-wrapper';

interface SelectRenderProps extends Pick<HTMLAttributes<HTMLDivElement>, 'className'> {
  attribute: Attribute;
  field: ControllerRenderProps<any>;
  isLoading?: boolean;
}

export function SelectRender({ attribute, className, field, isLoading }: SelectRenderProps) {
  const { disabled, id, model, placeholder, readOnly, selectCreatable = false } = attribute;

  const selectModelValues = new Set(
    (model ?? [])
      .map((item) => item?.value)
      .filter((item): item is string => typeof item === 'string' && item.trim() !== ''),
  );

  const selectCurrentValue = typeof field.value === 'string' ? field.value : '';
  const selectSanitizedValue =
    selectCreatable || selectModelValues.has(selectCurrentValue) ? selectCurrentValue : '';

  const hasInitializedWithInvalidValue =
    !isLoading &&
    field.value != null &&
    field.value !== '' &&
    selectSanitizedValue !== selectCurrentValue;

  useEffect(() => {
    if (!hasInitializedWithInvalidValue) return;
    field.onChange('');
  }, [field, hasInitializedWithInvalidValue]);

  const comboboxModel =
    model?.map((item) => ({
      ...item,
      item: attribute.renderModelItem?.(item) ?? item.item,
      key: typeof item.item === 'string' ? item.item : '',
    })) ?? [];

  if (selectCreatable && selectCurrentValue && !selectModelValues.has(selectCurrentValue)) {
    comboboxModel.push({
      item: selectCurrentValue,
      key: selectCurrentValue,
      value: selectCurrentValue,
    });
  }

  return (
    <FieldWrapper className={className} {...attribute}>
      <ComboboxModel
        disabled={disabled || readOnly || isLoading}
        id={id}
        model={comboboxModel}
        placeholder={placeholder}
        emptyOptionsText={selectCreatable ? (placeholder ?? 'Seleccionar') : undefined}
        searchPlaceholder={selectCreatable ? 'Buscar o escribir' : undefined}
        button={
          selectCreatable
            ? {
                text: 'Usar',
                onClick: (searchValue) => field.onChange(searchValue.trim()),
              }
            : undefined
        }
        value={selectSanitizedValue}
        onSelect={(value) =>
          field.onChange(selectCreatable || selectModelValues.has(value) ? value : '')
        }
      />
    </FieldWrapper>
  );
}
